import React from 'react';
import { ShieldCheck, Award, Zap, ThumbsUp, Star } from 'lucide-react';

export default function TrustScoreGauge({
  score = 98,
  rank = "Elite Designer",
  stats = {
    deliveryRate: "99.4%",
    avgResponse: "< 2 hrs",
    positiveReviews: "98.7%",
    totalSales: "1.4k+"
  }
}) {
  // SVG Radial parameters
  const radius = 54;
  const stroke = 8;
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '20px',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ShieldCheck size={20} color="var(--accent)" />
          <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)' }}>Trust & Credibility Score</span>
        </div>
        <span style={{
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--primary-text)',
          background: 'var(--primary-subtle)',
          padding: '4px 10px',
          borderRadius: '999px',
          border: '1px solid var(--primary-border)'
        }}>
          {rank}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
        {/* Radial Progress Gauge */}
        <div style={{ position: 'relative', width: '120px', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg height="120" width="120" style={{ transform: 'rotate(-90deg)' }}>
            {/* Background Track */}
            <circle
              stroke="var(--bg-surface-active)"
              fill="transparent"
              strokeWidth={stroke}
              r={normalizedRadius}
              cx="60"
              cy="60"
            />
            {/* Progress Stroke */}
            <circle
              stroke="url(#trustGradient)"
              fill="transparent"
              strokeWidth={stroke}
              strokeDasharray={circumference + ' ' + circumference}
              style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.8s ease-out' }}
              strokeLinecap="round"
              r={normalizedRadius}
              cx="60"
              cy="60"
            />
            <defs>
              <linearGradient id="trustGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="var(--primary)" />
                <stop offset="100%" stopColor="var(--accent)" />
              </linearGradient>
            </defs>
          </svg>

          {/* Centered Score */}
          <div style={{ position: 'absolute', textAlign: 'center' }}>
            <div style={{ fontSize: '1.65rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>
              {score}
            </div>
            <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginTop: '2px' }}>
              / 100
            </div>
          </div>
        </div>

        {/* Breakdown Metric Pills */}
        <div style={{ flex: 1, minWidth: '180px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={{ background: 'var(--bg-base)', padding: '10px 14px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '2px' }}>
              <Zap size={12} color="var(--primary)" /> Speed
            </div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>{stats.avgResponse}</div>
          </div>

          <div style={{ background: 'var(--bg-base)', padding: '10px 14px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '2px' }}>
              <Star size={12} color="var(--warning)" /> Ratings
            </div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>{stats.positiveReviews}</div>
          </div>

          <div style={{ background: 'var(--bg-base)', padding: '10px 14px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '2px' }}>
              <Award size={12} color="var(--accent)" /> Quality
            </div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>{stats.deliveryRate}</div>
          </div>

          <div style={{ background: 'var(--bg-base)', padding: '10px 14px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '2px' }}>
              <ThumbsUp size={12} color="var(--info)" /> Downloads
            </div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>{stats.totalSales}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
